import * as z from 'zod'
import {
    AgentBuiltInTool,
    CHAT_FILE_TOOLS,
    USER_ANSWERED_TOOLS,
    VOICE_RENDERED_TOOLS,
} from './Agent'
import type { UIChatMessage } from './Chat'

/**
 * Lifecycle of a wakeup (spec agent-wakeup). `running` to `skipped` follow the
 * run-ledger convention of the headless runs; `replaced` and `stopped` close a
 * wakeup that never ran.
 */
export const ChatWakeupStatus = {
    Scheduled: 'scheduled',
    Running: 'running',
    Succeeded: 'succeeded',
    Failed: 'failed',
    Skipped: 'skipped',
    /** A later call of the same chat took its place. */
    Replaced: 'replaced',
    /** `stop: true`, or the user's "Stop" in the chat. */
    Stopped: 'stopped',
} as const

export type ChatWakeupStatus =
    (typeof ChatWakeupStatus)[keyof typeof ChatWakeupStatus]

/** Where a platform wakeup posts its answer: the Chat SDK thread, not the channel. */
export const ChatWakeupDeliveryTargetSchema = z.object({
    platform: z.string(),
    threadId: z.string(),
})
export type ChatWakeupDeliveryTarget = z.infer<
    typeof ChatWakeupDeliveryTargetSchema
>

/** The wakeup still waiting in a chat, as the chat UI needs it. */
export const PendingWakeupSchema = z.object({
    wakeId: z.uuid(),
    dueAt: z.iso.datetime(),
    reason: z.string(),
})
export type PendingWakeup = z.infer<typeof PendingWakeupSchema>

export const ChatWakeupStateSchema = z.object({
    pendingWakeup: PendingWakeupSchema.nullable(),
})
export type ChatWakeupState = z.infer<typeof ChatWakeupStateSchema>

/** The body of a scheduled Service Bus message, and of the internal fire call. */
export const WakeupFireJobSchema = z.object({
    wakeId: z.uuid(),
})
export type WakeupFireJob = z.infer<typeof WakeupFireJobSchema>

/** Delays a wakeup may take, in seconds: the clamp of Claude Code's ScheduleWakeup. */
export const WAKEUP_MIN_DELAY_SECONDS = 60
export const WAKEUP_MAX_DELAY_SECONDS = 3600

/** The user message a wakeup injected: the agent's own prompt coming back, not the user's words. */
export const isWakeupSeed = (message?: UIChatMessage): boolean =>
    message?.role === 'user' && Boolean(message.metadata?.wakeup)

/** Tool states that still wait for something: the model's input or the user's approval. */
const PENDING_TOOL_STATES = new Set([
    'input-streaming',
    'input-available',
    'approval-requested',
])

/**
 * Tools the chat draws as a card: the client widgets, the cards that ask the
 * user something, and the four file tools, server tools whose output is the
 * download card of the new file or version.
 */
const CARD_TOOLS: ReadonlySet<string> = new Set([
    ...VOICE_RENDERED_TOOLS,
    ...USER_ANSWERED_TOOLS,
    ...CHAT_FILE_TOOLS,
])

/**
 * Cards that ask the user something. The contact form is one too, although
 * its lead goes through an endpoint of its own and never answers the turn.
 */
const QUESTION_TOOLS: ReadonlySet<string> = new Set([
    ...USER_ANSWERED_TOOLS,
    AgentBuiltInTool.ShowContactForm,
])

type MessagePart = UIChatMessage['parts'][number]

const stateOf = (part: MessagePart): string | undefined =>
    (part as { state?: string }).state

const toolNameOf = (part: MessagePart): string | undefined =>
    part.type.startsWith('tool-') ? part.type.slice('tool-'.length) : undefined

/**
 * Whether a wakeup answer shows the user anything: words, a file, a card, or a
 * tool call still waiting, such as one held for the user's approval. It is
 * what makes a round news (spec agent-wakeup, Fase 1.1), whatever its `noop`
 * said: the model's history keeps the round, the feed draws it and the user
 * gets a notification.
 */
export const wakeupAnswerShows = (message?: UIChatMessage): boolean =>
    message?.role === 'assistant' &&
    message.parts.some((part) => {
        if (part.type === 'text') {
            return part.text.trim().length > 0
        }
        if (part.type === 'file') {
            return true
        }
        const state = stateOf(part)
        if (state && PENDING_TOOL_STATES.has(state)) {
            return true
        }
        const toolName = toolNameOf(part)
        return (
            toolName !== undefined &&
            CARD_TOOLS.has(toolName) &&
            state === 'output-available'
        )
    })

/**
 * Whether a wakeup answer leaves the user a question: a card that asks
 * something, still unanswered. Such a round ends its loop, and the answer
 * opens a normal turn (spec agent-wakeup, "Una domanda ferma il ciclo").
 */
export const wakeupAnswerAsks = (message?: UIChatMessage): boolean =>
    message?.role === 'assistant' &&
    message.parts.some((part) => {
        const toolName = toolNameOf(part)
        return (
            toolName !== undefined &&
            QUESTION_TOOLS.has(toolName) &&
            stateOf(part) === 'input-available'
        )
    })

/**
 * Whether an answer still waits for the user to answer it in the chat: a card
 * answered through the turn, or a call held for the user's approval. A wakeup
 * round must not run below it, because the answer resumes the last message
 * only, and a history with a newer turn records the approval as denied. The
 * contact form does not count: its lead goes through an endpoint of its own,
 * wherever its card is, and its part never leaves `input-available`.
 */
export const answerAwaitsUser = (message?: UIChatMessage): boolean =>
    message?.role === 'assistant' &&
    message.parts.some((part) => {
        const state = stateOf(part)
        if (state === 'approval-requested') {
            return true
        }
        const toolName = toolNameOf(part)
        return (
            toolName !== undefined &&
            USER_ANSWERED_TOOLS.has(toolName) &&
            state === 'input-available'
        )
    })

/**
 * An answer that only told its loop nothing had changed: a finished
 * `scheduleWakeup` call with `noop`, and nothing shown to the user
 * ({@link wakeupAnswerShows}). The one rule shared by the model's history
 * (which drops such rounds) and the chat feed (which folds them).
 */
export const isQuietWakeupAnswer = (message?: UIChatMessage): boolean => {
    if (message?.role !== 'assistant' || wakeupAnswerShows(message)) {
        return false
    }
    let noop = false
    for (const part of message.parts) {
        if (part.type === 'tool-scheduleWakeup') {
            noop =
                stateOf(part) === 'output-available' &&
                (part as { input?: { noop?: boolean } }).input?.noop === true
        }
    }
    return noop
}
